import type { CatalogProduct, Metric } from "@/lib/comparison-catalog";
import { formatNumber } from "@/lib/i18n/format";
import type { Locale } from "@/lib/i18n/config";

/** Chart strings the shared dictionaries don't have yet. */
export const chartCopy = {
  es: {
    best: "Mejor valor", source: "Fuente", reviewed: (date: string) => `Revisado el ${date}`, rank: (n: number, total: number) => `#${n} de ${total}`,
    cheaper: "más barato", lower: "menor", more: "más", free: "sin costo", vsMedian: "vs. mediana", better: "mejor", sweetSpot: "Zona ideal",
    tableOf: (x: string, y: string) => `Valores de ${x} y ${y}`, product: "Producto",
  },
  en: {
    best: "Best value", source: "Source", reviewed: (date: string) => `Reviewed ${date}`, rank: (n: number, total: number) => `#${n} of ${total}`,
    cheaper: "cheaper", lower: "lower", more: "more", free: "free", vsMedian: "vs. median", better: "better", sweetSpot: "Sweet spot",
    tableOf: (x: string, y: string) => `${x} and ${y} values`, product: "Product",
  },
  pt: {
    best: "Melhor valor", source: "Fonte", reviewed: (date: string) => `Revisado em ${date}`, rank: (n: number, total: number) => `#${n} de ${total}`,
    cheaper: "mais barato", lower: "menor", more: "mais", free: "sem custo", vsMedian: "vs. mediana", better: "melhor", sweetSpot: "Zona ideal",
    tableOf: (x: string, y: string) => `Valores de ${x} e ${y}`, product: "Produto",
  },
} satisfies Record<Locale, unknown>;

/** Zero-based axis with round ticks: 4–6 intervals of 1, 2, 2.5 or 5 × 10ⁿ. */
export function niceScale(max: number) {
  if (!Number.isFinite(max) || max <= 0) return { top: 1, step: 0.25, ticks: [0, 0.25, 0.5, 0.75, 1] };
  const raw = max / 5;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step = ([1, 2, 2.5, 5, 10].find(n => n * magnitude >= raw) ?? 10) * magnitude;
  const count = Math.max(1, Math.ceil(max / step - 1e-9));
  const round = (n: number) => Number(n.toPrecision(12));
  return { top: round(step * count), step: round(step), ticks: Array.from({ length: count + 1 }, (_, i) => round(step * i)) };
}

export const valueOf = (product: CatalogProduct, metric: Metric) => product.values[metric.key] ?? null;

/** The best published value among `rows`, or null when the metric isn't comparable or nobody differs. */
export function bestValue(values: (number | null | undefined)[], metric: Metric) {
  const known = values.filter((n): n is number => n != null);
  if (metric.comparable === false || new Set(known).size < 2) return null;
  return metric.direction === "asc" ? Math.min(...known) : Math.max(...known);
}

/** Second-best distinct value (the one the winner is measured against). */
export function runnerUp(values: (number | null | undefined)[], metric: Metric) {
  const distinct = [...new Set(values.filter((n): n is number => n != null))].sort((a, b) => metric.direction === "asc" ? a - b : b - a);
  return distinct.length >= 2 ? distinct[1] : null;
}

export function median(values: number[]) {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/** Rank of `value` among `values` for the metric's direction; ties share the better rank. */
export function rankOf(value: number, values: number[], direction: "asc" | "desc") {
  return values.filter(other => direction === "asc" ? other < value : other > value).length + 1;
}

const ratioText = (ratio: number, locale: Locale) => `${formatNumber(locale, ratio, { maximumFractionDigits: ratio >= 10 ? 0 : 1 })}×`;
const pctText = (pct: number) => pct < 1 ? "<1%" : `${Math.round(pct)}%`;

/** How much better `better` is than `worse` ("3,2× más barato", "+18%"), or null when equal. */
export function advantage(better: number, worse: number, metric: Pick<Metric, "direction" | "kind">, locale: Locale) {
  const copy = chartCopy[locale];
  if (better === worse) return null;
  if (metric.direction === "asc") {
    const word = metric.kind === "money" ? copy.cheaper : copy.lower;
    if (better <= 0) return metric.kind === "money" ? copy.free : `100% ${word}`;
    const ratio = worse / better;
    return ratio >= 1.5 ? `${ratioText(ratio, locale)} ${word}` : `${pctText((1 - better / worse) * 100)} ${word}`;
  }
  if (worse <= 0) return `+${formatNumber(locale, better, { maximumFractionDigits: 2 })}`;
  const ratio = better / worse;
  return ratio >= 1.5 ? `${ratioText(ratio, locale)} ${copy.more}` : `+${pctText((ratio - 1) * 100)}`;
}

/** Signed gap of `value` from the best value, from the loser's side ("+150%", "−12%", "4,1×"). */
export function gapFromBest(value: number, best: number, locale: Locale) {
  if (value === best) return null;
  if (best === 0) return null;
  const ratio = value / best;
  if (ratio >= 2) return ratioText(ratio, locale);
  const pct = (ratio - 1) * 100;
  return `${pct > 0 ? "+" : "−"}${pctText(Math.abs(pct))}`;
}

/** Evidence label for one metric of one product, if the catalog links one. */
export function sourceFor(product: CatalogProduct, metric: Metric) {
  return product.sources.find(source => source.fields.includes(metric.key));
}

/** Full-precision figure for tooltips ("0.00695 USD / GB-mes"). */
export function exactValue(value: number, metric: Metric, locale: Locale) {
  return `${formatNumber(locale, value, { maximumFractionDigits: 6 })} ${metric.unit}`;
}
